import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { ICalendarWorkTemplateBase } from "@miplatform/types";
import { addAlpha } from "@miplatform/utils";
import { Box, Tooltip, Typography } from "@mui/material";
import type { TimeInterval } from "../types";

const parseTimeToHours = (time: string): number => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours + minutes / 60;
};

const buildWindowBlocks = (
  workTemplateBases: ICalendarWorkTemplateBase[],
  intervalCount: number
): AutoAssignWindowBlockData[] =>
  workTemplateBases.flatMap((template) =>
    template.autoAssignWindows.map((autoAssignWindow, windowIndex) => {
      const startHour = parseTimeToHours(autoAssignWindow.start);
      const endHour = parseTimeToHours(autoAssignWindow.end);
      const topIndex = Math.max(0, startHour);
      const bottomIndex = Math.min(intervalCount, endHour);

      return {
        key: `${template.id}-${windowIndex}`,
        shortName: template.shortName,
        color: addAlpha(template.color, 10),
        borderColor: template.color,
        topIndex,
        spanCount: Math.max(1, bottomIndex - topIndex),
      };
    })
  );

export function AutoAssignWindows({
  workTemplateBases = [],
  intervals = [],
  adjustedHeight = 0,
}: Readonly<AutoAssignWindowsProps>) {
  const { classes } = useStyles({ adjustedHeight });

  const blocks = useMemo(
    () => buildWindowBlocks(workTemplateBases, intervals.length),
    [workTemplateBases, intervals.length]
  );

  if (blocks.length === 0) return null;

  return (
    <Box className={classes.container} data-testid="auto-assign-windows">
      {blocks.map((block) => (
        <AutoAssignWindowBlock
          key={block.key}
          block={block}
          adjustedHeight={adjustedHeight}
        />
      ))}
    </Box>
  );
}

function AutoAssignWindowBlock({
  block,
  adjustedHeight,
}: Readonly<{
  block: AutoAssignWindowBlockData;
  adjustedHeight: number;
}>) {
  const { classes } = useWindowBlockStyles({
    top: block.topIndex * adjustedHeight,
    height: block.spanCount * adjustedHeight,
    backgroundColor: block.color,
    borderColor: block.borderColor,
  });

  return (
    <Tooltip title={block.shortName} placement="right">
      <Box className={classes.windowBlock}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.HD1}
          className={classes.label}
          color={block.borderColor}
          component="span"
        >
          {block.shortName}
        </Typography>
      </Box>
    </Tooltip>
  );
}

const useStyles = makeStyles<{ adjustedHeight: number }>()(
  (theme, { adjustedHeight }) => ({
    container: {
      position: "relative",
      width: 22,
      height: adjustedHeight > 0 ? "100%" : "auto",
      flexShrink: 0,
      backgroundColor: theme.palette.background.default,
    },
  })
);

const useWindowBlockStyles = makeStyles<{
  top: number;
  height: number;
  backgroundColor: string;
  borderColor: string;
}>()((theme, { top, height, backgroundColor, borderColor }) => ({
  windowBlock: {
    position: "absolute",
    left: 2,
    right: 0,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    borderRadius: 2,
    overflow: "hidden",
    top,
    height,
    backgroundColor,
    border: `1px solid ${borderColor}`,
  },
  label: {
    marginTop: theme.spacing(4),
    writingMode: "vertical-rl",
  },
}));

export interface AutoAssignWindowsProps {
  workTemplateBases?: ICalendarWorkTemplateBase[];
  intervals?: TimeInterval[];
  adjustedHeight?: number;
}

interface AutoAssignWindowBlockData {
  key: string;
  shortName: string;
  color: string;
  topIndex: number;
  spanCount: number;
  borderColor: string;
}
